import * as THREE from 'three/webgpu';
import { mix, oneMinus, texture, uniform, uv, vec2, vec3 } from 'three/tsl';

import { uNight, uPlayerPos } from '../core/uniforms.js';
import { warmGrade, hitFlashMix, weatherGrade } from '../render/nodes.js';
import { PLAYER_ASPECT, PLAYER_HEIGHT } from '../assets/playerSprite.js';
import { WalkWobble } from './WalkWobble.js';

/**
 * @file The player: a single billboard chibi with camera-relative movement.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MOVEMENT
 * ─────────────────────────────────────────────────────────────────────────────
 * WASD is read in CAMERA space and rotated by the camera yaw, so W always walks
 * away from the viewer no matter which of the four 90-degree yaws the rig is
 * snapped to. Accelerate at 42 m/s^2 while a key is held, clamp at
 * `maxSpeed * speedMul`, and bleed 14 m/s^2 when nothing is held — friction is
 * NOT applied while accelerating (42/14 would cap the real top speed at 3 m/s
 * and the tuning numbers would all be lies).
 *
 * ⚠ THAT CLAMP IS CONDITIONAL NOW, AND IT USED TO READ "6.5 m/s" FLAT. It is
 * 6.5 walking and 9.1 running: {@link Player#speedMul} is a plain multiplier
 * `systems/Gameplay.js` writes from `systems/Stamina.js` every step, 1.4 while
 * the sprint is held with a bar left to spend and EXACTLY 1 otherwise. This
 * class owns none of that policy — no key, no bar, no drain — it owns one
 * number, and that number is inert at 1. See the field's own note.
 *
 * Collision is `CollisionGrid.resolveCircle` when the level exposes one, which
 * slides along walls instead of stopping dead. The level is duck-typed
 * (`level.collision?.resolveCircle`) so this class works with a bare
 * `{ heightAt }` stub in tests and with a full generated level in the game.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DAMAGE, AND THE VEST OVER IT
 * ─────────────────────────────────────────────────────────────────────────────
 * `hurt(n)` is the ONE door for both damage and healing (`n < 0`). Armour —
 * bought at a gunsmith's till, `systems/Shops.js` — sits in front of hp and is
 * spent before it: `min(armour, n)` comes off the vest and only the remainder
 * reaches health. It is INERT AT ZERO by construction (the absorb is inside an
 * `armour > 0` branch), which is what lets it exist on a path pinned by hero
 * mode's golden checksum.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SPRITE
 * ─────────────────────────────────────────────────────────────────────────────
 * One `THREE.Sprite` + `SpriteNodeMaterial`, `center = (0.5, 0)` so the pivot is
 * at the feet: the sprite stands on `heightAt(x,z)` exactly (no z-fighting, no
 * floating), and `WalkWobble`'s rocking pivots around the feet like a real
 * waddle instead of around the belly.
 *
 * ⚠ DEVIATION: mirroring flips the UVs, NOT `scale.x`. The brief says "negate
 * scale.x", but `SpriteNodeMaterial` derives its scale from
 * `modelWorldMatrix[0].xyz.length()` — a LENGTH, so a negative object scale is
 * silently ignored — and a negative `scaleNode.x` reverses the quad's winding,
 * which back-face culling then eats. Flipping `uv.x` is the only mirror that
 * actually works here, and it costs one uniform.
 *
 * Tint: `LightProbeGrid` day+night samples (10 Hz, they change slowly and
 * trilinear sampling is not free) into two colour uniforms, blended by `uNight`
 * in the shader — so the chibi darkens in alleys and warms under lit windows at
 * zero per-frame GPU cost. Levels with no probe grid fall back to constants.
 *
 * @module engine/actors/Player
 */

/** @typedef {import('../types.js').Level} Level */
/** @typedef {import('../types.js').LightProbeGrid} LightProbeGrid */

export class Player {
  /**
   * @param {Object} o
   * @param {Level|{heightAt?: Function, probes?: LightProbeGrid|null, collision?: any}|null} [o.level]
   *   Anything with `heightAt` / `probes` / `collision`. All optional.
   * @param {import('../input/Keyboard.js').Input|null} [o.input]
   * @param {THREE.Camera|null} [o.camera] Used only to derive the yaw when
   *   `getCameraYaw` is not supplied.
   * @param {(() => number)|null} [o.getCameraYaw] Preferred: the live rig yaw in
   *   radians. Cheaper and exact (the camera matrix lags by a frame).
   * @param {THREE.Texture|null} [o.texture] From `assets/playerSprite.js`. May be
   *   null (tests, and a level that boots before its art) — the sprite then
   *   renders as a flat quad.
   * @param {number} [o.aspect=PLAYER_ASPECT] Sprite width/height (the shipped
   *   chibi human is 260/406). Only affects WIDTH — the pivot is the feet.
   * @param {{x: number, y: number, z: number}} [o.spawn]
   * @param {Object} [o.opts] Tuning; also accepted flat on `o` for convenience.
   * @param {number} [o.opts.height=PLAYER_HEIGHT] World height in metres.
   * @param {number} [o.opts.radius=0.35]
   * @param {number} [o.opts.accel=42]
   * @param {number} [o.opts.maxSpeed=6.5]
   * @param {number} [o.opts.speedMul=1] Multiplier on `maxSpeed` — the sprint.
   *   1 is the identity and the default; see {@link Player#speedMul}.
   * @param {number} [o.opts.friction=14]
   * @param {number} [o.opts.hp=100]
   * @param {number} [o.opts.maxArmour=100] Ceiling for {@link Player#addArmour}.
   *   The player always STARTS with zero of it.
   * @param {number} [o.opts.flashDecay=0.15] Seconds for a hit flash to fade.
   * @param {any} [o.opts.collision] Explicit collision grid (overrides
   *   `level.collision`).
   * @param {[number,number,number]} [o.opts.ambientDay=[1,1,1]] Tint when the
   *   level has no probe grid.
   * @param {[number,number,number]} [o.opts.ambientNight=[0.32,0.38,0.55]]
   * @param {number} [o.opts.probeHz=10] Probe resample rate.
   * @param {Object} [o.opts.wobble] Passed to {@link WalkWobble}.
   * @param {number} [o.opts.alphaTest=0.5]
   */
  constructor(o = {}) {
    const opts = { ...o, ...(o.opts ?? {}) };
    const {
      level = null,
      input = null,
      camera = null,
      getCameraYaw = null,
      texture: map = null,
      aspect = PLAYER_ASPECT,
      spawn = null,
      height = PLAYER_HEIGHT,
      radius = 0.35,
      accel = 42,
      maxSpeed = 6.5,
      speedMul = 1,
      friction = 14,
      hp = 100,
      maxArmour = 100,
      flashDecay = 0.15,
      collision = null,
      ambientDay = [1, 1, 1],
      ambientNight = [0.32, 0.38, 0.55],
      probeHz = 10,
      wobble = null,
      alphaTest = 0.5,
      name = 'player',
    } = opts;

    /** @type {any} */
    this.level = level;
    /** @type {import('../input/Keyboard.js').Input|null} */
    this.input = input;
    /** @type {THREE.Camera|null} */
    this.camera = camera;
    /** @type {(() => number)|null} */
    this.getCameraYaw = getCameraYaw;

    this.height = height;
    this.radius = radius;
    this.accel = accel;
    this.maxSpeed = maxSpeed;
    /**
     * @type {number} Multiplier on {@link maxSpeed} — THE SPRINT, and the only
     * thing it scales. `systems/Gameplay.js` writes `stamina.speedMul` here on
     * EVERY step (`STAMINA.mul` 1.4 while the run key is held with a bar left to
     * spend, exactly 1 otherwise), which is why a stale 1.4 cannot survive into
     * the step after the player gets into a car.
     *
     * It is the same mechanism the rest of the cast has had all along, spelt the
     * same way: `EnemyManager#setSpeedMul` is the per-slot version, and
     * `FOLLOW.speedMul`/`FOLLOW.sprintMul` (`systems/Followers.js`) is the escort
     * riding it. One idea, one shape, three callers — the player was simply the
     * one actor that could not be told to run.
     *
     * ⚠ INERT AT 1 BY CONSTRUCTION, and that is a contract in exactly the sense
     * {@link armour} is INERT AT ZERO: the clamp is `this.maxSpeed *
     * this.speedMul`, and `x * 1` is bit-identical in IEEE-754, so every caller
     * that never writes this — every dev scene, every suite, and hero mode's
     * golden rng checksum (1878938342, `tests/hero.test.mjs`) — takes the
     * arithmetic it took before the sprint existed, down to the ULP. The
     * multiplication is NOT behind an `if`, because it does not need to be.
     *
     * ⚠ IT SCALES `maxSpeed` ONLY, NEVER {@link accel} — a decision, not an
     * omission. At 42 m/s² the mover is at 6.5 m/s in ~0.15 s and at 9.1 in
     * ~0.22 s, so scaling the acceleration alongside it would be a second knob
     * whose entire observable effect is ~70 ms of ramp nobody can feel. One
     * number moves the top speed; nothing else moves at all.
     */
    this.speedMul = speedMul;
    this.friction = friction;
    this.flashDecay = flashDecay;

    /** @type {number} */
    this.hp = hp;
    /** @type {number} */
    this.maxHp = hp;
    /**
     * @type {number} Body armour, bought at a gunsmith's till
     * (`systems/Shops.js`). Damage eats this BEFORE it reaches {@link hp}.
     *
     * ⚠ INERT AT ZERO, and that is a contract rather than an accident: with
     * `armour === 0` {@link hurt} takes exactly the arithmetic it took before
     * armour existed, so hero mode's golden rng checksum cannot move and no
     * level that never sells armour behaves differently by a single ULP.
     * `tests/shops.test.mjs` section (m) proves it against a transcription of
     * the old expression.
     */
    this.armour = 0;
    /** @type {number} Ceiling for {@link addArmour}. One full vest. */
    this.maxArmour = maxArmour;
    /** @type {number} 0..1, decays after {@link hurt}. */
    this.flash = 0;
    /** @type {boolean} Set by {@link hurt} when hp hits 0. */
    this.dead = false;

    /** @type {THREE.Vector3} World position; Y is the GROUND height (feet). */
    this.position = new THREE.Vector3(spawn?.x ?? 0, spawn?.y ?? 0, spawn?.z ?? 0);
    /** @type {THREE.Vector3} World velocity (Y unused — this is a 2D mover). */
    this.velocity = new THREE.Vector3();
    /** @type {number} Horizontal speed, m/s. */
    this.speed = 0;
    /** @type {1|-1} Which way the art faces in CAMERA space (+1 = right, as authored). */
    this.facing = 1;
    /** @type {boolean} True on the step the player was pushed out of geometry. */
    this.hitWall = false;
    /** @type {number} Camera yaw used by the last {@link update}. */
    this.yaw = 0;

    /** @type {WalkWobble} */
    this.wobble = new WalkWobble(wobble ?? {});

    /**
     * Draw-only shift along the view axis — see {@link setDepthBias} and the
     * long note in `render/Billboards.js`. Zero until `systems/Gameplay.js`
     * feeds it, and zero is the identity.
     * @type {{x: number, y: number, z: number}}
     */
    this._depthBias = { x: 0, y: 0, z: 0 };

    // ── uniforms ─────────────────────────────────────────────────────────────
    this._uFlash = uniform(0);
    /** 0 = art as authored (facing right), 1 = mirrored. */
    this._uFlip = uniform(0);
    this._uTintD = uniform(new THREE.Color(ambientDay[0], ambientDay[1], ambientDay[2]));
    this._uTintN = uniform(new THREE.Color(ambientNight[0], ambientNight[1], ambientNight[2]));
    this._ambientDay = ambientDay;
    this._ambientNight = ambientNight;
    this._probeHz = probeHz;
    this._probeAccum = 1; // sample on the very first update
    this._probeTmp = new THREE.Color();
    /** Indoor light override — see {@link Player#setLightOverride}.
     *  @type {{day: ArrayLike<number>, night: ArrayLike<number>}|null} */
    this._lightOverride = null;

    // ── sprite ───────────────────────────────────────────────────────────────
    const mat = new THREE.SpriteNodeMaterial();
    mat.name = name;
    // Cutout, not blended: alpha-tested silhouettes depth-write, so the chibi
    // sorts correctly against the world with no render-order bookkeeping.
    mat.transparent = false;
    mat.depthWrite = true;
    mat.alphaTest = alphaTest;
    mat.fog = true;
    mat.vertexColors = false;
    mat.sizeAttenuation = true;

    const base = uv();
    const flipped = vec2(mix(base.x, oneMinus(base.x), this._uFlip), base.y);
    const tex = map ? texture(map, flipped) : null;

    // `weatherGrade` by hand — same reason as `render/Billboards.js`: this is a
    // probe-sampled AMBIENT tint, not a baked vertex colour, so it does not pass
    // through `dayNightVertexColor` where the cloud/lightning grade lives.
    const ambient = weatherGrade(mix(vec3(this._uTintD).mul(warmGrade()), vec3(this._uTintN), uNight));
    let color = tex ? tex.rgb.mul(ambient) : vec3(ambient);
    color = hitFlashMix(this._uFlash, color);
    mat.colorNode = color;
    if (tex) mat.opacityNode = tex.a;

    /** @type {THREE.Sprite} */
    this.object = new THREE.Sprite(mat);
    this.object.name = name;
    this.object.center.set(0.5, 0); // pivot at the feet
    this.object.scale.set(height * aspect, height, 1);
    this.object.frustumCulled = true;
    /** @type {THREE.SpriteNodeMaterial} */
    this.material = mat;
    /** @type {THREE.Texture|null} */
    this.texture = map;

    this._axis = { x: 0, z: 0 };
    /** Scratch for `resolveCircle` — this runs 60x a second, per actor. */
    this._probePos = { x: 0, z: 0 };
    this._probe = /** @type {LightProbeGrid|null} */ (level?.probes ?? null);
    this._collision = collision ?? level?.collision ?? null;
    /** @type {((x: number, z: number) => number)|null} Overrides `level.heightAt`. */
    this._heightAt = null;
    /** Construction-time collision, latched by the first {@link setEnvironment}. */
    this._baseCollision = null;
    this._envSet = false;
    this._disposed = false;

    this.snapToGround();
    this._syncObject();
  }

  // ── state ──────────────────────────────────────────────────────────────────

  /**
   * @returns {number} 0..1 — a PUBLIC contract, clamped against the WALKING
   *   {@link maxSpeed} and deliberately left that way: a sprinting player reads
   *   1 here, not 1.4. ⚠ It is no longer what feeds {@link wobble}; that call
   *   needs the un-clamped ratio to see the sprint at all (see it, and
   *   `WalkWobble`'s header).
   */
  get speed01() {
    return this.maxSpeed > 0 ? Math.min(1, this.speed / this.maxSpeed) : 0;
  }

  /**
   * Applies damage and fires the hit flash.
   *
   * ARMOUR ABSORBS FIRST and only the remainder reaches hp — a hit that armour
   * eats whole still FLASHES, because the player was shot and the feedback is
   * about the shot, not about the bookkeeping. Healing (`n < 0`) never touches
   * armour in either direction: a sandwich is not a vest.
   *
   * ⚠ The absorb is behind `armour > 0`, so with no armour this method is the
   * expression it always was — see the field's own note.
   *
   * @param {number} n Damage. Negative heals (and does not flash).
   * @returns {number} Remaining hp.
   */
  hurt(n) {
    if (n > 0) {
      if (this.armour > 0) {
        const absorbed = Math.min(this.armour, n);
        this.armour -= absorbed;
        n -= absorbed;
      }
      this.hp = Math.max(0, this.hp - n);
      this.flash = 1;
      if (this.hp === 0) this.dead = true;
    } else {
      this.hp = Math.min(this.maxHp, this.hp - n);
      if (this.hp > 0) this.dead = false;
    }
    return this.hp;
  }

  /**
   * Puts a vest on. The gunsmith's till (`systems/Shops.js`) is the only caller
   * today; it asks first whether this would do anything, so a player at the
   * ceiling is never sold one.
   * @param {number} n Points of armour. A non-finite or non-positive value is
   *   ignored rather than clamped — `addArmour(-50)` must not be a way to strip
   *   armour, which is {@link clearArmour}'s job and nobody else's.
   * @returns {number} The new armour value.
   */
  addArmour(n) {
    if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return this.armour;
    this.armour = Math.min(this.maxArmour, this.armour + n);
    return this.armour;
  }

  /**
   * Strips it. Called on the RESPAWN (`systems/Gameplay.js`) and nowhere else:
   * you do not wake up in the plaza still wearing the vest you died in, and no
   * healing path may take it off you.
   */
  clearArmour() {
    this.armour = 0;
  }

  /**
   * Teleports (respawn, level entry). Clears velocity and the walk cycle.
   * @param {number} x @param {number} z @param {number} [y] Defaults to the
   *   sampled ground height.
   */
  moveTo(x, z, y) {
    this.position.x = x;
    this.position.z = z;
    this.velocity.set(0, 0, 0);
    this.speed = 0;
    this.wobble.reset();
    if (y === undefined) this.snapToGround();
    else this.position.y = y;
    this._syncObject();
  }

  /**
   * The player is being CARRIED by something else — today, the car they are
   * driving (`systems/Driving.js`). Position and velocity are written straight
   * through, so the camera rig, the minimap and every proximity effect keep
   * tracking "the player" without any of them learning what a vehicle is.
   *
   * This exists rather than {@link moveTo} because `moveTo` zeroes the velocity,
   * and a camera with no velocity to look ahead along makes 17 m/s feel like a
   * slideshow. The walk cycle is deliberately NOT advanced: whatever is doing
   * the carrying owns the animation.
   *
   * @param {number} x @param {number} y Feet height. @param {number} z
   * @param {number} [vx=0] @param {number} [vz=0]
   */
  carry(x, y, z, vx = 0, vz = 0) {
    this.position.set(x, y, z);
    this.velocity.x = vx;
    this.velocity.z = vz;
    this.speed = Math.hypot(vx, vz);
    this._syncObject();
    uPlayerPos.value.set(x, y, z);
  }

  /**
   * Shows/hides the on-foot billboard. Getting into a car hides it; nothing
   * else should touch it.
   * @param {boolean} v
   */
  setVisible(v) {
    this.object.visible = !!v;
  }

  /**
   * Moves the mover into a different world — an interior room, which owns its
   * own collision grid and sits on a flat floor far below the terrain. Only the
   * two things the mover reads per step are swapped; the sprite, the probes and
   * every tuning number are untouched.
   *
   * @param {Object} [env]
   * @param {any} [env.collision] Grid exposing `resolveCircle`. Null/absent
   *   means the room has no walls at all.
   * @param {((x: number, z: number) => number)|null} [env.heightAt] Floor height
   *   source for {@link snapToGround}. Null falls back to `level.heightAt`.
   */
  setEnvironment(env = {}) {
    // Latch the construction-time pair on the FIRST call only: walking from one
    // room straight into another must still restore the CITY, not room one.
    if (!this._envSet) {
      this._baseCollision = this._collision;
      this._envSet = true;
    }
    this._collision = env.collision ?? null;
    this._heightAt = typeof env.heightAt === 'function' ? env.heightAt : null;
  }

  /** Restores the construction-time collision and height source. Idempotent. */
  clearEnvironment() {
    if (!this._envSet) return;
    this._collision = this._baseCollision;
    this._baseCollision = null;
    this._heightAt = null;
    this._envSet = false;
  }

  /** Re-samples the current height source (`level.heightAt` unless overridden). */
  snapToGround() {
    const h = this._heightAt ?? this.level?.heightAt;
    if (typeof h === 'function') this.position.y = h(this.position.x, this.position.z);
  }

  // ── frame ──────────────────────────────────────────────────────────────────

  /**
   * One fixed step.
   * @param {number} dt Seconds.
   */
  update(dt) {
    if (this._disposed || dt <= 0) return;

    const yaw = this._cameraYaw();
    this.yaw = yaw;

    // ── 1. camera-space input -> world direction ────────────────────────────
    const axis = this.input ? this.input.axis(this._axis) : zero(this._axis);
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);
    // The camera orbits to `target + (sin(yaw), _, cos(yaw)) * d`, so it looks
    // along `-(sin, cos)`: forward = (-sin, -cos), right = (cos, -sin).
    const wx = axis.x * cos + axis.z * sin;
    const wz = -axis.x * sin + axis.z * cos;
    const inLen = Math.hypot(wx, wz);
    const moving = inLen > 1e-4;

    // ── 2. accelerate / brake ───────────────────────────────────────────────
    let vx = this.velocity.x;
    let vz = this.velocity.z;

    if (moving) {
      // `axis` is already clamped to length <= 1, so a half-deflected stick
      // accelerates at half rate for free.
      vx += wx * this.accel * dt;
      vz += wz * this.accel * dt;
      const sp = Math.hypot(vx, vz);
      // ⚠ THE SPRINT LIVES HERE AND NOWHERE ELSE IN THIS CLASS. `accel` above
      // is deliberately un-scaled — see {@link Player#speedMul} — and with the
      // multiplier at its default 1 this is `this.maxSpeed` to the ULP.
      const top = this.maxSpeed * this.speedMul;
      if (sp > top) {
        const k = top / sp;
        vx *= k;
        vz *= k;
      }
    } else {
      const sp = Math.hypot(vx, vz);
      if (sp > 0) {
        const next = Math.max(0, sp - this.friction * dt);
        const k = next > 0 ? next / sp : 0;
        vx *= k;
        vz *= k;
      }
    }

    // ── 3. integrate + resolve ──────────────────────────────────────────────
    let px = this.position.x + vx * dt;
    let pz = this.position.z + vz * dt;

    this.hitWall = false;
    const grid = this._collision;
    if (grid && typeof grid.resolveCircle === 'function') {
      this._probePos.x = px;
      this._probePos.z = pz;
      const r = grid.resolveCircle(this._probePos, this.radius, 2);
      if (r.hit) {
        // Kill only the velocity component that drove us into the wall; the
        // tangential part survives, which is what makes corners feel smooth.
        const vn = vx * r.nx + vz * r.nz;
        if (vn < 0) {
          vx -= r.nx * vn;
          vz -= r.nz * vn;
        }
        this.hitWall = true;
      }
      px = r.x;
      pz = r.z;
    }

    this.position.x = px;
    this.position.z = pz;
    this.velocity.x = vx;
    this.velocity.z = vz;
    this.speed = Math.hypot(vx, vz);

    // ── 4. ground ───────────────────────────────────────────────────────────
    this.snapToGround();

    // ── 5. facing (camera space) ────────────────────────────────────────────
    // Screen-right component of the velocity: dot(v, right).
    const screenX = vx * cos - vz * sin;
    if (Math.abs(screenX) > 0.08) this.facing = screenX > 0 ? 1 : -1;
    this._uFlip.value = this.facing < 0 ? 1 : 0;

    // ── 6. animation + flash ────────────────────────────────────────────────
    // ⚠ THE GAIT IS THE SPRINT'S ONLY TELL, and it is fed the UN-CLAMPED ratio
    // on purpose. `speed01` saturates at the WALKING 6.5 m/s and `WalkWobble`
    // clamps again internally, so routing the sprint through it made walking
    // and running bit-identical on screen — a run key with no animation at all.
    // The wobble is handed the ceiling (`maxScale`) and the raw ratio, and it
    // decides what may exceed 1: the CADENCE does, the AMPLITUDE does not.
    // A plain field write rather than a fifth `update()` argument — that method
    // has many callers and none of the others has legs that can sprint.
    // ⚠ At `speedMul` 1 this is the expression it always was: the ratio is the
    // unclamped half of `speed01`, and `maxScale` 1 re-applies the clamp with
    // the identical comparison.
    this.wobble.maxScale = this.speedMul;
    this.wobble.update(dt, this.maxSpeed > 0 ? this.speed / this.maxSpeed : 0, moving);
    if (this.flash > 0) {
      this.flash = this.flashDecay > 0 ? Math.max(0, this.flash - dt / this.flashDecay) : 0;
    }
    this._uFlash.value = this.flash;

    // ── 7. ambient probe (throttled) ────────────────────────────────────────
    this._probeAccum += dt;
    const interval = this._probeHz > 0 ? 1 / this._probeHz : Infinity;
    if (this._probeAccum >= interval) {
      this._probeAccum = 0;
      this._sampleProbes();
    }

    this._syncObject();
    uPlayerPos.value.set(this.position.x, this.position.y, this.position.z);
  }

  /**
   * Biases the DRAWN sprite toward the camera so the geometry behind the player
   * stops eating their head — the whole mechanism, the measurements and the
   * trade-off are in `render/Billboards.js`'s header, and the vector comes from
   * its `cameraDepthBias(rig.yaw, rig.pitch)`.
   *
   * The player is a lone `THREE.Sprite` rather than a `SpriteBatch` instance, so
   * it takes the same maths on the CPU: one add in {@link _syncObject}, scaled
   * by {@link height} exactly as the shader scales by `iScale.y`. Applied
   * immediately, because this is called from the draw-state step AFTER
   * `update()` has already synced this step's position.
   *
   * ⚠ {@link position} — and therefore `uPlayerPos`, collision, the camera rig
   * and every blip — is UNTOUCHED. Only `object.position` moves.
   *
   * @param {number} [x] @param {number} [y] @param {number} [z] Metres per
   *   metre of player height. All-zero restores the un-biased sprite exactly.
   */
  setDepthBias(x = 0, y = 0, z = 0) {
    this._depthBias.x = x;
    this._depthBias.y = y;
    this._depthBias.z = z;
    this._syncObject();
  }

  /** Pushes position/wobble onto the sprite. Split out so `moveTo` can reuse it. */
  _syncObject() {
    const b = this._depthBias;
    const h = this.height;
    this.object.position.set(
      this.position.x + b.x * h,
      this.position.y + this.wobble.bob + b.y * h,
      this.position.z + b.z * h,
    );
    // `materialRotation` reads `material.rotation` live — no uniform needed, and
    // the rotation pivots on `center`, i.e. the feet.
    this.material.rotation = this.wobble.tilt;
  }

  /** @returns {number} Radians. */
  _cameraYaw() {
    if (typeof this.getCameraYaw === 'function') return this.getCameraYaw();
    const cam = this.camera;
    if (cam) {
      // Yaw of the vector from the target to the camera, matching the Engine's
      // orbit convention (`atan2(x, z)`, not `atan2(z, x)`).
      const e = cam.matrixWorld.elements;
      return Math.atan2(e[8], e[10]);
    }
    return 0;
  }

  /**
   * Replaces the probe sample with a fixed day/night pair — the INDOOR light.
   *
   * The probe grid is layered UPWARD from street level and clamps in Y, so a
   * player in a shop room at y = -100 would sample whatever street sits 100 m
   * overhead — dark at night inside a lamp-lit room. `systems/Interiors.js`
   * passes the room's own pre-solved probe (`plan.light`) at SWAP-in and null
   * at swap-out; `abort()` routes through swap-out, so the override can never
   * outlive the room. Passing null (or a plan without `light`) restores the
   * grid sample on the next frame.
   *
   * @param {{day: ArrayLike<number>, night: ArrayLike<number>}|null} light
   */
  setLightOverride(light) {
    this._lightOverride = light?.day && light?.night ? light : null;
  }

  /** Trilinear day+night ambient at the player's chest. */
  _sampleProbes() {
    const o = this._lightOverride;
    if (o) {
      this._uTintD.value.setRGB(o.day[0], o.day[1], o.day[2]);
      this._uTintN.value.setRGB(o.night[0], o.night[1], o.night[2]);
      return;
    }
    const probes = this._probe;
    if (!probes || typeof probes.sample !== 'function') return;
    const x = this.position.x;
    const y = this.position.y + this.height * 0.55;
    const z = this.position.z;
    // `sample(..., nightAmt, out)` mixes internally, so 0 and 1 give us the two
    // channels separately and the SHADER does the crossfade — which keeps the
    // sprite in step with the world's `uNight` between samples.
    probes.sample(x, y, z, 0, this._probeTmp);
    this._uTintD.value.copy(this._probeTmp);
    probes.sample(x, y, z, 1, this._probeTmp);
    this._uTintN.value.copy(this._probeTmp);
  }

  // ── teardown ───────────────────────────────────────────────────────────────

  /** Idempotent. Does NOT dispose the texture (the asset cache owns it). */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.object.removeFromParent();
    this.material.dispose();
  }
}

/**
 * @param {{x: number, z: number}} a
 * @returns {{x: number, z: number}}
 */
function zero(a) {
  a.x = 0;
  a.z = 0;
  return a;
}
